import { DOCUMENT } from '@angular/common';
import { inject, Injectable, OnDestroy } from '@angular/core';
import { Event as RouterEvent, NavigationError, Router } from '@angular/router';
import { Subscription } from 'rxjs';
import { ErrorNotificationService } from '../../../shared/services/error-notification-service/error-notification.service';

const CHUNK_LOAD_ERROR_MESSAGE =
  'Не удалось загрузить страницу. Проверьте подключение к интернету. После восстановления соединения страница обновится.';

const CHUNK_LOAD_ERROR_PATTERNS = [
  /Failed to fetch dynamically imported module/i,
  /Importing a module script failed/i,
  /error loading dynamically imported module/i,
  /Loading chunk [\d]+ failed/i,
  /ChunkLoadError/i,
];

@Injectable({
  providedIn: 'root',
})
export class ChunkLoadErrorRecoveryService implements OnDestroy {
  private readonly router = inject(Router);
  private readonly errorNotificationService = inject(ErrorNotificationService);
  private readonly browserWindow = inject(DOCUMENT).defaultView!;
  private readonly routerSubscription = new Subscription();
  private isInitialized = false;
  private isWaitingForConnection = false;
  private readonly reloadAfterConnectionRestored = (): void => {
    this.isWaitingForConnection = false;
    this.browserWindow.location.reload();
  };

  startMonitoringChunkLoadErrors(): void {
    if (this.isInitialized) {
      return;
    }

    this.isInitialized = true;
    this.routerSubscription.add(this.router.events.subscribe(this.handleRouterEvent.bind(this)));
  }

  ngOnDestroy(): void {
    this.routerSubscription.unsubscribe();
    this.browserWindow.removeEventListener('online', this.reloadAfterConnectionRestored);
  }

  private handleChunkLoadError(): void {
    this.errorNotificationService.showError(CHUNK_LOAD_ERROR_MESSAGE);

    if (this.browserWindow.navigator.onLine || this.isWaitingForConnection) {
      return;
    }

    this.isWaitingForConnection = true;
    this.browserWindow.addEventListener('online', this.reloadAfterConnectionRestored, {
      once: true,
    });
  }

  private handleRouterEvent(event: RouterEvent): void {
    if (
      event instanceof NavigationError &&
      event.error instanceof Error &&
      this.isChunkLoadError(event.error)
    ) {
      this.handleChunkLoadError();
    }
  }

  private isChunkLoadError(error: Error): boolean {
    return CHUNK_LOAD_ERROR_PATTERNS.some((pattern): boolean => pattern.test(error.message));
  }
}
